'use client';
import { useState, useMemo, useCallback, useRef } from 'react';
import MaybeLink from '@/components/apt/MaybeLink';
import { fmtAmount } from '@/lib/format';
import SearchInput from '@/components/SearchInput';
import { complexCardCells, complexCardCols, complexCardFillers } from './complex-card-cells';
import '@/components/apt/sub/apt-sub.css';

interface Complex {
  aptName: string; sigungu: string; region: string; dong: string;
  builtYear: number; saleCount: number; rentCount: number;
  lastPrice: number; jeonse: number; monthly: number; monthlyRent: number;
  ageGroup: string; jeonseRatio: number | null;
  pyeongPrice: number; hasCoords: boolean;
  imageUrl?: string | null;
  /** ⑲ⓒ K-5 — 단지백과 링크(색인 대상일 때만). 서버(page.tsx · /api/complex-search)가 채운다. 없으면 링크 없음. */
  complexHref?: string | null;
}

/* ── 원형 게이지 ── */
function Gauge({ ratio, size = 40 }: { ratio: number; size?: number }) {
  const r = (size - 4) / 2, circ = 2 * Math.PI * r;
  const pct = Math.min(ratio, 100) / 100;
  const color = ratio > 80 ? 'var(--accent-red)' : ratio > 60 ? 'var(--accent-yellow)' : 'var(--accent-green)';
  return (
    <svg className="as-cx-gauge" width={size} height={size} role="img" aria-label={`전세가율 ${ratio}%`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth="3" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3"
        strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text data-rd="label" x={size / 2} y={size / 2 + 4} textAnchor="middle" fill={color} fontSize="12" fontWeight="700">{ratio}%</text>
    </svg>
  );
}

export default function ComplexClient({ complexes, ageGroups, regions, initialRegion, ageChartData }: {
  complexes: Complex[]; ageGroups: string[]; regions: string[];
  initialRegion?: string | null;
  ageChartData?: { group: string; avg: number; count: number }[];
}) {
  const [selectedAge, setSelectedAge] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'saleCount' | 'lastPrice' | 'jeonseRatio' | 'pyeongPrice'>('saleCount');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Complex[] | null>(null);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await fetch(`/api/complex-search?q=${encodeURIComponent(q)}`);
      const { results } = await res.json();
      setSearchResults(results);
    } catch { setSearchResults(null); }
    setSearching(false);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (value.length < 2) { setSearchResults(null); return; }
    timerRef.current = setTimeout(() => doSearch(value), 300);
  }, [doSearch]);

  const displayData = searchResults || complexes;
  const filtered = useMemo(() => {
    let result = displayData;
    if (!searchResults) {
      if (selectedAge) result = result.filter((c: any) => c.ageGroup === selectedAge);
      if (!initialRegion && selectedRegion) result = result.filter((c: any) => c.region === selectedRegion);
    }
    return result.sort((a: any, b: any) => {
      if (sortBy === 'lastPrice') return (b.lastPrice || 0) - (a.lastPrice || 0);
      if (sortBy === 'jeonseRatio') return (b.jeonseRatio || 0) - (a.jeonseRatio || 0);
      if (sortBy === 'pyeongPrice') return (b.pyeongPrice || 0) - (a.pyeongPrice || 0);
      return (b.saleCount + b.rentCount) - (a.saleCount + a.rentCount);
    });
  }, [displayData, searchResults, selectedAge, selectedRegion, sortBy, initialRegion]);

  /* VX L4-6 — 단지 계열 문법(as-*): 연차 칩(36px · 선택 = 네이비) + 정렬 → 연차별 평균 타일 → 검색 + 건수 → 단지 카드.
     상태·검색·정렬·필터 로직과 카드 정보(매매 · 전세가율 · 부가 칸 · 거래 건수)는 종전 그대로.
     걷은 것: 연차별 원값 색 7종 × 3벌 · 그라데이션 막대 · 12px 미만 글자 · 인라인 스타일. 카드 사진은 기존 체인 그대로(기축 — 상상도 대상 아님). */
  return (
    <>
      {/* 연차 칩 + 정렬 */}
      <div className="as-cx-bar">
        <div className="as-chips" role="group" aria-label="연차">
          <button type="button" className="as-chip" aria-pressed={!selectedAge} onClick={() => setSelectedAge(null)}>전체</button>
          {ageGroups.map((g: any) => {
            const active = selectedAge === g;
            return (
              <button type="button" key={g} className="as-chip" aria-pressed={active} onClick={() => setSelectedAge(active ? null : g)} data-rd="label">{g}</button>
            );
          })}
        </div>
        <select className="as-select" aria-label="정렬" value={sortBy} onChange={(e: any) => setSortBy(e.target.value)}>
          <option value="saleCount">거래순</option>
          <option value="lastPrice">매매가순</option>
          <option value="pyeongPrice">평당가순</option>
          <option value="jeonseRatio">전세가율순</option>
        </select>
      </div>

      {/* 연차별 평균 매매가 — 누르면 그 연차만 */}
      {ageChartData && (
        <ul className="as-tiles as-cx-ages" aria-label="연차별 평균 매매가">
          {ageChartData.map((a: any) => {
            const on = selectedAge === a.group;
            return (
              <li key={a.group}>
                <button type="button" className="as-tile" aria-pressed={on} onClick={() => setSelectedAge(on ? null : a.group)}>
                  <span className="as-tile-k" data-rd="label">{a.group}</span>
                  {a.avg > 0 ? <span className="as-tile-v">{fmtAmount(a.avg)}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* 검색 + 카운트 */}
      <div className="as-cx-search">
        <SearchInput
          value={searchQuery}
          onChange={v => handleSearch(v)}
          placeholder="단지명 검색"
          size="sm"
          loading={searching}
        />
        <span className="as-sec-n" data-rd="label"><b>{filtered.length.toLocaleString()}</b>개</span>
      </div>

      {/* 단지 카드 격자 */}
      <div className="as-grid">
        {filtered.length === 0 && (
          <p className="as-empty as-cx-empty">검색 결과가 없습니다. 다른 검색어나 필터를 시도해보세요.</p>
        )}
        {filtered.map((c: any, i: number) => {
          return (
            <MaybeLink key={`${c.aptName}__${c.sigungu || i}`} as="div" href={c.complexHref} className="hero-card as-cx">
              {/* 사진(소형) — 기존 체인 그대로 */}
              <div className="hero-img hero-img-sm">
                <img src={c.imageUrl || `/api/og?title=${encodeURIComponent(c.aptName)}&category=apt&design=${(i % 6) + 1}`} alt={c.aptName} width={300} height={90} loading="lazy" />
                <div className="hero-badges">
                  <span className="hero-badge as-cx-badge" data-rd="label">{c.ageGroup}</span>
                </div>
                {/* TOP 표식 */}
                {i < 3 && !searchResults && !selectedAge && (
                  <div className="hero-chip">
                    <span className="hero-badge as-cx-badge as-cx-badge--top" data-rd="label">TOP {i + 1}</span>
                  </div>
                )}
                <div className="hero-overlay">
                  <div className="hero-name">{c.aptName}</div>
                  <div className="hero-addr" data-rd="label">{[c.region, c.sigungu, c.dong].filter(Boolean).join(' ')}{c.builtYear > 0 ? ` · ${c.builtYear}년` : ''}</div>
                </div>
              </div>

              {/* 매매 크게 + 원형 게이지 — b27 레인 A(R1): 매매가가 없으면 「매매 —」 대신 그 조각이 없다. 둘 다 없으면 줄째 없다. */}
              {(c.lastPrice > 0 || c.jeonseRatio) ? (
              <div className="as-cx-main">
                {c.lastPrice > 0 ? (
                <div data-complex-sale="">
                  <div className="as-cx-k" data-rd="label">매매</div>
                  <div className="as-cx-v">{fmtAmount(c.lastPrice)}</div>
                </div>
                ) : <span />}
                {c.jeonseRatio ? <Gauge ratio={c.jeonseRatio} size={42} /> : null}
              </div>
              ) : null}

              {/* 3열 부가 데이터 — b27 레인 A(R1): 값 없는 칸(「전세 —」「갭투자 —」)은 칸째 뺀다. 칸이 0 이면 격자도 없다.
                  마지막 줄의 빈 자리는 글자 없는 채움 칸(배경만)으로 메운다 — 격자 배경(테두리색)이 덩어리로 보이지 않게. */}
              {(() => {
                const cells = complexCardCells(c);
                if (cells.length === 0) return null;
                const fillers = complexCardFillers(cells.length);
                return (
                  <div className="as-cx-cells" data-complex-cells={cells.length} data-cols={complexCardCols(cells.length)}>
                    {cells.map(p => (
                      <div key={p.label}>
                        <div className="as-cx-k" data-rd="label">{p.label}</div>
                        <div className="as-cx-c">{p.value}</div>
                      </div>
                    ))}
                    {Array.from({ length: fillers }, (_, k) => (
                      <div key={`fill-${k}`} aria-hidden="true" />
                    ))}
                  </div>
                );
              })()}

              {/* 하단 — 거래 0건이면 건수 조각이 없다(R1) */}
              <div className="as-cx-foot" data-rd="label">
                {((c.saleCount || 0) + (c.rentCount || 0)) > 0
                  ? <span data-rd="label">거래 <b>{((c.saleCount || 0) + (c.rentCount || 0)).toLocaleString()}</b>건</span>
                  : <span />}
                {c.complexHref ? <span className="as-cx-go" data-rd="label">상세 →</span> : null}
              </div>
            </MaybeLink>
          );
        })}
      </div>

      {/* MASTER v3 §2(2026-09-26): LoginGate(가짜 미리보기 블러+카카오 CTA) 제거. */}
    </>
  );
}
